import { useRef } from "react";
import { Row, Col } from "react-bootstrap";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { snakeCaseToSentenceCase } from "utils/formatText";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import { useAppSelector } from "../../../redux/hooks";
import { MARKETPLACE_TO_STORE_PAGE_URL_PREFIX_LOOKUP } from "utils/marketplaceConstants";

const AmazonProfilesTable = ({
  tableData,
  columnsToRender,
  isLoading,
  additionalColumns = [],
  titleColWidth = null,
  defaultSorted,
  isSelectable = false,
  selectedProfiles = [],
  setSelectedProfiles = () => {},
  selectionKey = "id",
  hideBulkActionsButton = false,
  bulkActionOptions = [],
  defaultPageSize = 10,
  showIds = false,
  hideSearchFilter = false,
  hideBottomPagination = false,
}) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableRef = useRef();

  const columnOptions = [
    buildTextColumn("name", "Organization Name"),
    buildTextColumn("marketplaces", "Profile Marketplaces", {
      formatter: (v) => {
        return v.join(", ");
      },
    }),
    buildTextColumn("profile_type", "Profile Type", {
      formatter: (v) => {
        return snakeCaseToSentenceCase(v);
      },
    }),
    buildTextColumn("amazon_user_email", "Authorized By User Email", {
      checked: false,
    }),
    buildTextColumn("amazon_user_name", "Authorized By User Name", { checked: false }),
    buildTextColumn("amazon_user_id", "Authorized By User ID", {
      checked: false,
    }),
    buildTextColumn("status", "Status", {
      formatter: (v) => {
        return snakeCaseToSentenceCase(v);
      },
      width: 150,
    }),
    {
      key: "details",
      options: {
        className: "text-start justify-content-start px-5 h-100",
        cellContainerClasses: "text-start justify-content-start",
        metric: {
          id: "details",
          format: "text",
          accessor: (d) => d,
          name: "Seller ID",
        },
        formatter: (v) => {
          if (v.profile_type !== "seller") {
            return;
          }

          const storePagePrefix = MARKETPLACE_TO_STORE_PAGE_URL_PREFIX_LOOKUP[v.marketplaces[0]];

          return (
            <a href={`${storePagePrefix}${v.entity_id}`} target={"_blank"} rel="noreferrer">
              {v.entity_id}
            </a>
          );
        },
        width: 250,
      },
    },

    ...additionalColumns,
  ];

  if (showIds) {
    columnOptions.unshift(buildTextColumn("id", "Org ID"));
  }

  const columnSpecs = columnsToRender.map((renderColumnKey) =>
    columnOptions.find((option) => option.key === renderColumnKey),
  );

  return (
    <Row className="mx-0 w-100">
      <Col xs={12} className="mx-auto px-0">
        <BulkActionsTable
          marketPlace={marketPlace}
          tableRef={tableRef}
          tableId={"trellisAmazonProfilesTable"}
          titleCol={{
            id: "name",
            isStatic: true,
            Header: "Profile Name",
            accessor: "profile_name",
            style: { width: "450px" },
            width: titleColWidth ? titleColWidth : 250,
            Cell: ({ row }) => {
              const { profile_name } = row._original;

              return <div className="d-flex align-items-center overflow-ellipsis">{profile_name}</div>;
            },
            filterMethod: () => {
              return;
            },
            Filter: () => null,
          }}
          columnSpecs={columnSpecs}
          data={tableData}
          bulkActionOptions={bulkActionOptions}
          hideFilterToggleIcon={true}
          hideBulkActionsButton={hideBulkActionsButton}
          isSelectable={isSelectable}
          isExpandable={false}
          selected={selectedProfiles}
          setSelected={setSelectedProfiles}
          selectionKey={selectionKey}
          defaultSorted={defaultSorted || [{ id: "name", desc: false }]}
          defaultPageSize={defaultPageSize}
          getSearchCriteria={(row) => {
            const { id, name, profile_name, marketplaces, profile_type, account_manager } = row;
            return `${id} ${name} ${profile_name} ${marketplaces} ${profile_type} ${account_manager}`;
          }}
          tableRowDescription={"Amazon Profile"}
          minRows={0}
          isLoading={isLoading}
          hideSearchFilter={hideSearchFilter}
          hideBottomPagination={hideBottomPagination}
        />
      </Col>
    </Row>
  );
};

export default AmazonProfilesTable;
